import { Row, Col, Button, Form, Modal as AntModal, Checkbox } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import Constants from "../../Utils/Constants";
import useToken from "../../Authentication/useToken";
import PropTypes from "prop-types";
import "./Styles.css";
import { useTranslation } from "react-i18next";
import CreateClinicModal from "../../Clinic/CreateClinicModal";
import { useEffect, useState } from "react";
import ProfessionalIdentityFields from "../shared/ProfessionalIdentityFields";
import ClinicSelectorField from "../shared/ClinicSelectorField";
import { isClinicManagerRole } from "../shared/roleUtils";

const kinesiologistFieldKeys = {
  labelFirstName: "Kinesiologist.label_first_name",
  placeholderFirstName: "Kinesiologist.placeholder_first_name",
  errorRequiredFirstName: "Kinesiologist.error_required_first_name",
  errorFirstNameMinLength: "Kinesiologist.error_first_name_min_length",
  labelLastName: "Kinesiologist.label_last_name",
  placeholderLastName: "Kinesiologist.placeholder_last_name",
  errorRequiredLastName: "Kinesiologist.error_required_last_name",
  errorLastNameMinLength: "Kinesiologist.error_last_name_min_length",
  labelEmail: "Kinesiologist.label_email",
  placeholderEmail: "Kinesiologist.placeholder_email",
  errorRequiredEmail: "Kinesiologist.error_required_email",
  errorInvalidEmailFormat: "Kinesiologist.error_invalid_email_format",
  labelConfirmEmail: "Kinesiologist.label_confirm_email",
  placeholderConfirmEmail: "Kinesiologist.placeholder_confirm_email",
  errorRequiredEmailConfirmation: "Kinesiologist.error_required_email_confirmation",
  errorEmailMismatch: "Kinesiologist.error_email_mismatch",
  labelPhoneNumber: "Kinesiologist.label_phone_number",
  placeholderPhoneNumber: "Kinesiologist.placeholder_phone_number",
  errorRequiredPhoneNumber: "Kinesiologist.error_required_phone_number",
  errorInvalidPhoneNumber: "Kinesiologist.error_invalid_phone_number",
};

function CreateKinesiologist({ refetchKinesiologists }) {
  const { t } = useTranslation("Professionals");
  const isClinicManager = isClinicManagerRole();

  const { token } = useToken();
  const [clinics, setClinics] = useState([]);
  const [clinicModalOpen, setClinicModalOpen] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();

  const openModal = (msg, isError) => {
    AntModal[isError ? "error" : "success"]({
      content: <p>{msg}</p>,
      okText: "Close",
      centered: true,
      onOk: () => {
        if (!isError) reset();
      },
    });
  };

  const fetchClinics = async () => {
    try {
      const res = await axios.get(`${Constants.SERVER_URL}/clinics?active=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const clinicsData = res.data || [];
      setClinics(clinicsData);
      if (isClinicManager && clinicsData.length === 1) {
        setValue("clinicId", clinicsData[0].id, { shouldValidate: true });
      }
    } catch (e) {
      openModal(e?.response?.data?.message || "Erreur lors du chargement des cliniques", true);
    }
  };

  useEffect(() => {
    if (token) fetchClinics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSubmit = (data) => {
    const kinesiologistData = {
      firstname: data.firstname,
      lastname: data.lastname,
      email: data.email,
      phoneNumber: data.phoneNumber,
      role: "kinesiologist",
      clinicId: data.clinicId,
      assignManagerRole: Boolean(data.assignManagerRole),
    };

    axios
      .post(`${Constants.SERVER_URL}/create-professional-user`, kinesiologistData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        refetchKinesiologists();
        openModal(t("Kinesiologist.success_creating_msg"), false);
      })
      .catch((err) => {
        const backendMsg = err?.response?.data?.message;
        openModal(
          (backendMsg && t(`Backend:${backendMsg}`)) ||
            t("Kinesiologist.error_creating_kinesiologist"),
          true
        );
      });
  };

  return (
    <Row justify="center">
      <Col span={16}>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <ProfessionalIdentityFields
            t={t}
            control={control}
            errors={errors}
            getValues={getValues}
            keys={kinesiologistFieldKeys}
          />

          <Row gutter={16}>
            <Col span={12}>
              <ClinicSelectorField
                control={control}
                errors={errors}
                clinics={clinics}
                isClinicManager={isClinicManager}
                onOpenCreateClinic={() => setClinicModalOpen(true)}
              />

              {!isClinicManager && (
                <CreateClinicModal
                  open={clinicModalOpen}
                  onClose={() => setClinicModalOpen(false)}
                  onCreated={(clinic) => {
                    fetchClinics();
                    if (clinic?.id) setValue("clinicId", clinic.id);
                  }}
                />
              )}
            </Col>
          </Row>

          <Form.Item>
            <Controller
              name="assignManagerRole"
              control={control}
              defaultValue={false}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  checked={Boolean(value)}
                  onChange={(e) => onChange(e.target.checked)}
                >
                  Attribuer aussi le role de gestionnaire
                </Checkbox>
              )}
            />
          </Form.Item>

          <Form.Item className="submit-button">
            <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
              {t("Kinesiologist.button_create_kinesiologist")}
            </Button>
          </Form.Item>
        </Form>
      </Col>
    </Row>
  );
}

CreateKinesiologist.propTypes = {
  refetchKinesiologists: PropTypes.func.isRequired,
};

export default CreateKinesiologist;

